import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  TimePicker,
  Typography,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { useLanguage } from '../contexts/LanguageContext';
import { getUserPrimaryCompanyId, hasRole } from '../utils/permissionUtils';
import { buildCommerceAuditColumns, useCommerceUserTimezone } from '../utils/commerceListDateUtils';

const { Title, Text } = Typography;

const WEEKDAY_OPTIONS = [
  { value: 0, label: '週日 (0)' },
  { value: 1, label: '週一 (1)' },
  { value: 2, label: '週二 (2)' },
  { value: 3, label: '週三 (3)' },
  { value: 4, label: '週四 (4)' },
  { value: 5, label: '週五 (5)' },
  { value: 6, label: '週六 (6)' },
];

const DELIVERY_WEEKDAY_ABBR = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

function formatDeliveryWeekday(dateStr) {
  if (!dateStr) return '';
  const d = dayjs(String(dateStr).slice(0, 10));
  return d.isValid() ? DELIVERY_WEEKDAY_ABBR[d.day()] : '';
}

export default function CommerceDeliveryRulesPage() {
  const { t } = useLanguage();
  const userTimezoneOffset = useCommerceUserTimezone();
  const userInfo = typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('userInfo') || '{}') : {};
  const isTenantAdmin = hasRole(userInfo, 'Tenant_Admin');

  const [companiesForSelect, setCompaniesForSelect] = useState([]);
  const [companyFilter, setCompanyFilter] = useState(() => getUserPrimaryCompanyId());

  const [calendarRows, setCalendarRows] = useState([]);
  const [calendarYearFilter, setCalendarYearFilter] = useState(null);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [weekRows, setWeekRows] = useState([]);
  const [weekLoading, setWeekLoading] = useState(false);

  const [calModalOpen, setCalModalOpen] = useState(false);
  const [calEditing, setCalEditing] = useState(null);
  const [calForm] = Form.useForm();

  const [weekModalOpen, setWeekModalOpen] = useState(false);
  const [weekEditing, setWeekEditing] = useState(null);
  const [weekForm] = Form.useForm();
  const [weekCreateOpen, setWeekCreateOpen] = useState(false);
  const [weekCreateForm] = Form.useForm();

  const fetchWithAuth = useCallback((url, options = {}) => {
    const token = localStorage.getItem('token');
    const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };
    if (token) headers.Authorization = `Bearer ${token}`;
    return fetch(url, { ...options, headers });
  }, []);

  const loadCompanies = useCallback(async () => {
    try {
      const res = await fetchWithAuth('/api/commerce/companies-for-select');
      if (!res.ok) return;
      const data = await res.json();
      setCompaniesForSelect(Array.isArray(data) ? data : []);
    } catch {
      /* ignore */
    }
  }, [fetchWithAuth]);

  const loadCalendar = useCallback(async () => {
    setCalendarLoading(true);
    try {
      const q = companyFilter ? `?companyId=${companyFilter}` : '';
      const res = await fetchWithAuth(`/api/commerce/delivery-calendar-dates${q}`);
      if (res.status === 403) {
        message.error(t('commerceDeliveryRules.noPermission') || '無權限');
        setCalendarRows([]);
        return;
      }
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setCalendarRows(Array.isArray(data) ? data : []);
    } catch (e) {
      message.error((t('commerceDeliveryRules.loadFailed') || '載入失敗') + `: ${e.message}`);
      setCalendarRows([]);
    } finally {
      setCalendarLoading(false);
    }
  }, [fetchWithAuth, companyFilter, t]);

  const loadWeek = useCallback(async () => {
    setWeekLoading(true);
    try {
      const q = companyFilter ? `?companyId=${companyFilter}` : '';
      const res = await fetchWithAuth(`/api/commerce/delivery-weekday-cutoffs${q}`);
      if (res.status === 403) {
        message.error(t('commerceDeliveryRules.noPermission') || '無權限');
        setWeekRows([]);
        return;
      }
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setWeekRows(Array.isArray(data) ? data : []);
    } catch (e) {
      message.error((t('commerceDeliveryRules.loadFailed') || '載入失敗') + `: ${e.message}`);
      setWeekRows([]);
    } finally {
      setWeekLoading(false);
    }
  }, [fetchWithAuth, companyFilter, t]);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  useEffect(() => {
    loadCalendar();
    loadWeek();
  }, [loadCalendar, loadWeek]);

  const openCalCreate = () => {
    setCalEditing(null);
    calForm.resetFields();
    calForm.setFieldsValue({ is_holiday: false });
    setCalModalOpen(true);
  };

  const openCalEdit = (row) => {
    setCalEditing(row);
    const ot = row.orderCutoffTime;
    let timeVal = null;
    if (ot != null) {
      const ts = formatTs(ot);
      timeVal = dayjs(`2000-01-01T${ts}`);
    }
    calForm.setFieldsValue({
      delivery_date: row.deliveryDate ? dayjs(row.deliveryDate) : null,
      description_en: row.descriptionEn,
      description_zh: row.descriptionZh,
      is_holiday: row.isHoliday,
      order_cutoff_date: row.orderCutoffDate ? dayjs(row.orderCutoffDate) : null,
      order_cutoff_time: timeVal,
    });
    setCalModalOpen(true);
  };

  const submitCal = async () => {
    try {
      const v = await calForm.validateFields();
      const body = {
        deliveryDate: v.delivery_date.format('YYYY-MM-DD'),
        descriptionEn: v.description_en || null,
        descriptionZh: v.description_zh || null,
        isHoliday: !!v.is_holiday,
        orderCutoffDate: v.order_cutoff_date.format('YYYY-MM-DD'),
        orderCutoffTime: v.order_cutoff_time ? v.order_cutoff_time.format('HH:mm:ss') : '00:00:00',
      };
      if (isTenantAdmin && companyFilter) body.companyId = companyFilter;

      const url = calEditing
        ? `/api/commerce/delivery-calendar-dates/${calEditing.id}`
        : '/api/commerce/delivery-calendar-dates';
      const method = calEditing ? 'PUT' : 'POST';
      const res = await fetchWithAuth(url, { method, body: JSON.stringify(body) });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || err.message || res.statusText);
      }
      message.success(t('commerceDeliveryRules.saved') || '已儲存');
      setCalModalOpen(false);
      loadCalendar();
    } catch (e) {
      if (e.errorFields) return;
      message.error(e.message || 'Error');
    }
  };

  const deleteCal = async (id) => {
    const res = await fetchWithAuth(`/api/commerce/delivery-calendar-dates/${id}`, { method: 'DELETE' });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      message.error(err.error || 'Delete failed');
      return;
    }
    message.success(t('commerceDeliveryRules.deleted') || '已刪除');
    loadCalendar();
  };

  const openWeekEdit = (row) => {
    setWeekEditing(row);
    weekForm.setFieldsValue({
      cutoff_offset_seconds: row.cutoffOffsetSeconds,
    });
    setWeekModalOpen(true);
  };

  const submitWeek = async () => {
    try {
      const v = await weekForm.validateFields();
      if (!weekEditing) return;
      const res = await fetchWithAuth(`/api/commerce/delivery-weekday-cutoffs/${weekEditing.id}`, {
        method: 'PUT',
        body: JSON.stringify({ cutoffOffsetSeconds: v.cutoff_offset_seconds }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || err.message || res.statusText);
      }
      message.success(t('commerceDeliveryRules.saved') || '已儲存');
      setWeekModalOpen(false);
      loadWeek();
    } catch (e) {
      if (e.errorFields) return;
      message.error(e.message || 'Error');
    }
  };

  const openWeekCreate = () => {
    weekCreateForm.resetFields();
    setWeekCreateOpen(true);
  };

  const submitWeekCreate = async () => {
    try {
      const v = await weekCreateForm.validateFields();
      const body = {
        weekday: v.weekday,
        cutoffOffsetSeconds: v.cutoff_offset_seconds,
      };
      if (isTenantAdmin && companyFilter) body.companyId = companyFilter;
      const res = await fetchWithAuth('/api/commerce/delivery-weekday-cutoffs', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || err.message || 'Failed');
      }
      message.success(t('commerceDeliveryRules.saved') || '已儲存');
      setWeekCreateOpen(false);
      loadWeek();
    } catch (e) {
      if (e.errorFields) return;
      message.error(e.message || 'Error');
    }
  };

  const calendarYearOptions = useMemo(() => {
    const years = new Set();
    calendarRows.forEach((r) => {
      if (!r.deliveryDate) return;
      const d = dayjs(String(r.deliveryDate).slice(0, 10));
      if (d.isValid()) years.add(d.year());
    });
    return Array.from(years)
      .sort((a, b) => b - a)
      .map((y) => ({ value: y, label: String(y) }));
  }, [calendarRows]);

  const filteredCalendarRows = useMemo(() => {
    let rows = calendarRows;
    if (calendarYearFilter != null) {
      rows = rows.filter((r) => {
        if (!r.deliveryDate) return false;
        const d = dayjs(String(r.deliveryDate).slice(0, 10));
        return d.isValid() && d.year() === calendarYearFilter;
      });
    }
    return [...rows].sort((a, b) => {
      const da = a.deliveryDate ? String(a.deliveryDate).slice(0, 10) : '';
      const db = b.deliveryDate ? String(b.deliveryDate).slice(0, 10) : '';
      return da.localeCompare(db);
    });
  }, [calendarRows, calendarYearFilter]);

  const calColumns = useMemo(
    () => [
      {
        title: t('commerceDeliveryRules.colDeliveryDate') || '送貨日',
        dataIndex: 'deliveryDate',
        render: (_, r) => (r.deliveryDate ? String(r.deliveryDate).slice(0, 10) : ''),
        sorter: (a, b) => {
          const da = a.deliveryDate ? String(a.deliveryDate).slice(0, 10) : '';
          const db = b.deliveryDate ? String(b.deliveryDate).slice(0, 10) : '';
          return da.localeCompare(db);
        },
        defaultSortOrder: 'ascend',
      },
      {
        title: t('commerceDeliveryRules.colDeliveryWeekday') || '星期',
        key: 'deliveryWeekday',
        width: 72,
        render: (_, r) => formatDeliveryWeekday(r.deliveryDate),
      },
      { title: t('commerceDeliveryRules.descriptionEn'), dataIndex: 'descriptionEn', ellipsis: true },
      { title: t('commerceDeliveryRules.descriptionZh'), dataIndex: 'descriptionZh', ellipsis: true },
      {
        title: t('commerceDeliveryRules.colHoliday') || '假日',
        dataIndex: 'isHoliday',
        render: (v) => (v ? 'Y' : 'N'),
      },
      {
        title: t('commerceDeliveryRules.colCutoff') || '截單日時',
        render: (_, r) =>
          `${r.orderCutoffDate ? String(r.orderCutoffDate).slice(0, 10) : ''} ${formatTs(r.orderCutoffTime)}`.trim(),
      },
      ...buildCommerceAuditColumns(t, userTimezoneOffset),
      {
        title: '',
        key: 'actions',
        width: 100,
        fixed: 'right',
        render: (_, r) => (
          <Space>
            <Button type="link" icon={<EditOutlined />} onClick={() => openCalEdit(r)} />
            <Popconfirm
              title={t('commerceDeliveryRules.deleteCalendarConfirm') || '確定刪除？'}
              onConfirm={() => deleteCal(r.id)}
            >
              <Button type="link" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [t, userTimezoneOffset]
  );

  const weekColumns = useMemo(
    () => [
      {
        title: t('commerceDeliveryRules.colWeekday') || '星期',
        dataIndex: 'weekday',
        render: (w) => WEEKDAY_OPTIONS.find((o) => o.value === w)?.label ?? w,
      },
      {
        title: t('commerceDeliveryRules.cutoffOffsetSeconds') || '截單偏移（秒）',
        dataIndex: 'cutoffOffsetSeconds',
      },
      ...buildCommerceAuditColumns(t, userTimezoneOffset),
      {
        title: '',
        key: 'a',
        width: 80,
        fixed: 'right',
        render: (_, r) => (
          <Button type="link" icon={<EditOutlined />} onClick={() => openWeekEdit(r)} />
        ),
      },
    ],
    [t, userTimezoneOffset]
  );

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>{t('menu.commerceDeliveryRules') || '送貨截單規則'}</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        {t('commerceDeliveryRules.hint') ||
          '特殊送貨日以列表為準；未列日期則依每週截單秒數。公司時區請於公司資料設定（timezone）。'}
      </Text>

      {isTenantAdmin && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space wrap>
            <span>{t('commerceDeliveryRules.companyFilter') || '公司'}</span>
            <Select
              allowClear
              placeholder="—"
              style={{ minWidth: 220 }}
              value={companyFilter}
              onChange={setCompanyFilter}
              options={companiesForSelect.map((c) => ({ value: c.id, label: c.name }))}
            />
            <Button icon={<ReloadOutlined />} onClick={() => { loadCalendar(); loadWeek(); }}>
              {t('common.refresh') || '重新整理'}
            </Button>
          </Space>
        </Card>
      )}

      <Tabs
        items={[
          {
            key: 'cal',
            label: t('commerceDeliveryRules.tabCalendar') || '特殊送貨日／截單',
            children: (
              <Card>
                <Space style={{ marginBottom: 12 }} wrap>
                  <Button type="primary" icon={<PlusOutlined />} onClick={openCalCreate}>
                    {t('common.add') || '新增'}
                  </Button>
                  <span>{t('commerceDeliveryRules.yearFilter') || '送貨日年份'}</span>
                  <Select
                    allowClear
                    placeholder={t('commerceDeliveryRules.yearFilterAll') || '全部年份'}
                    style={{ width: 120 }}
                    value={calendarYearFilter}
                    onChange={setCalendarYearFilter}
                    options={calendarYearOptions}
                  />
                </Space>
                <Table
                  rowKey="id"
                  loading={calendarLoading}
                  dataSource={filteredCalendarRows}
                  columns={calColumns}
                  pagination={{ pageSize: 20 }}
                />
              </Card>
            ),
          },
          {
            key: 'week',
            label: t('commerceDeliveryRules.tabWeekly') || '每週截單（秒）',
            children: (
              <Card>
                <Space style={{ marginBottom: 12 }}>
                  <Button icon={<PlusOutlined />} onClick={openWeekCreate}>
                    {t('commerceDeliveryRules.addWeekday') || '新增星期'}
                  </Button>
                </Space>
                <Table
                  rowKey="id"
                  loading={weekLoading}
                  dataSource={weekRows}
                  columns={weekColumns}
                  pagination={false}
                />
              </Card>
            ),
          },
        ]}
      />

      <Modal
        title={calEditing ? (t('common.edit') || '編輯') : (t('common.add') || '新增')}
        open={calModalOpen}
        onCancel={() => setCalModalOpen(false)}
        onOk={submitCal}
        width={560}
        destroyOnClose
      >
        <Form form={calForm} layout="vertical">
          <Form.Item name="delivery_date" label={t('commerceDeliveryRules.colDeliveryDate') || '送貨日'} rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="description_en" label={t('commerceDeliveryRules.descriptionEn')}>
            <Input />
          </Form.Item>
          <Form.Item name="description_zh" label={t('commerceDeliveryRules.descriptionZh')}>
            <Input />
          </Form.Item>
          <Form.Item name="is_holiday" label={t('commerceDeliveryRules.colHoliday') || 'Is holiday'} valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="order_cutoff_date" label={t('commerceDeliveryRules.orderCutoffDate') || '截單日'} rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="order_cutoff_time" label={t('commerceDeliveryRules.orderCutoffTime') || '截單時間'} rules={[{ required: true }]}>
            <TimePicker style={{ width: '100%' }} format="HH:mm:ss" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t('commerceDeliveryRules.editWeek') || '編輯週截單'}
        open={weekModalOpen}
        onCancel={() => setWeekModalOpen(false)}
        onOk={submitWeek}
        destroyOnClose
      >
        <Form form={weekForm} layout="vertical">
          <Form.Item
            name="cutoff_offset_seconds"
            label={t('commerceDeliveryRules.cutoffOffsetSeconds') || '截單偏移（秒）'}
            rules={[{ required: true }]}
          >
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t('commerceDeliveryRules.addWeekdayTitle') || '新增星期截單'}
        open={weekCreateOpen}
        onCancel={() => setWeekCreateOpen(false)}
        onOk={submitWeekCreate}
        destroyOnClose
      >
        <Form form={weekCreateForm} layout="vertical">
          <Form.Item
            name="weekday"
            label={t('commerceDeliveryRules.weekdayField') || '星期（0–6）'}
            rules={[{ required: true }]}
          >
            <Select
              options={WEEKDAY_OPTIONS}
              placeholder={t('commerceDeliveryRules.weekdaySelectPlaceholder') || '0=Sun … 6=Sat'}
            />
          </Form.Item>
          <Form.Item
            name="cutoff_offset_seconds"
            label={t('commerceDeliveryRules.cutoffOffsetSeconds') || '截單偏移（秒）'}
            rules={[{ required: true }]}
          >
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

function formatTs(ts) {
  if (ts == null) return '';
  if (typeof ts === 'string') {
    if (ts.startsWith('PT')) {
      const m = ts.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?/);
      if (!m) return ts;
      const h = parseInt(m[1] || '0', 10);
      const mm = parseInt(m[2] || '0', 10);
      const s = Math.floor(parseFloat(m[3] || '0'));
      return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    }
    return ts.length >= 8 ? ts.slice(0, 8) : ts;
  }
  return String(ts);
}
